import { Box, Button, Typography } from '@mui/material';
import { useEffect } from 'react';

import { CurrencyDropDown, InvoiceExpenseType } from 'openapi';

import { Modal } from 'components/shared/Modal/Modal';

import { useTranslations } from 'context/TranslationContext';

import {
  modalSx,
  simulatorContainerStyle,
  simulatorContentStyle,
  simulatorEmptyStateStyle,
  simulatorEmptyTextStyle,
  simulatorStatusStyle,
  simulatorCloseButtonStyle,
  resultsSectionStyle
} from 'styles/pages/ApprovalRuleSimulatorStyles';

import { ApprovalRuleSimulatorForm } from './components/ApprovalRuleSimulatorForm';
import { EvaluationTraceSection } from './components/EvaluationTraceSection';
import { FinalGatesSection } from './components/FinalGatesSection';
import { MatchedRulesSection } from './components/MatchedRulesSection';
import { ResultsSummary } from './components/ResultsSummary';
import { SuppressedRulesSection } from './components/SuppressedRulesSection';
import { useApprovalRuleSimulator } from '../../../utils/helpers/useApprovalRuleSimulator';
import { SimulatorFormValues } from '../../../utils/interfaces/ApprovalSimulator';

interface ApprovalRuleSimulatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  expenseTypes?: InvoiceExpenseType[];
  counterparties?: Array<{ id: number; name: string }>;
  currencies?: CurrencyDropDown[];
  defaultCurrencyId?: number;
  defaultCurrency?: CurrencyDropDown;
  activeRulesCount?: number;
}

const getActualDefaultCurrency = ({
  defaultCurrency,
  defaultCurrencyId,
  currencies
}: {
  defaultCurrency?: CurrencyDropDown;
  defaultCurrencyId?: number;
  currencies?: CurrencyDropDown[];
}): CurrencyDropDown | undefined => {
  if (defaultCurrency) {
    return defaultCurrency;
  }

  if (defaultCurrencyId && currencies && currencies.length > 0) {
    return currencies.find((c) => c.id === defaultCurrencyId) || currencies[0];
  }

  return currencies?.[0];
};

export const ApprovalRuleSimulatorModal = ({
  isOpen,
  onClose,
  companyId,
  expenseTypes,
  counterparties,
  currencies,
  defaultCurrencyId,
  defaultCurrency,
  activeRulesCount = 0
}: ApprovalRuleSimulatorModalProps) => {
  const { translate } = useTranslations();

  const { simulationResult, runSimulation, resetSimulation } =
    useApprovalRuleSimulator({ companyId });

  const actualDefaultCurrency = getActualDefaultCurrency({
    defaultCurrency,
    defaultCurrencyId,
    currencies
  });

  const handleSimulateClick = async (formValues: SimulatorFormValues) => {
    await runSimulation(formValues);
  };

  const handleSimulationChange = () => {
    resetSimulation();
  };

  useEffect(() => {
    if (!isOpen) {
      resetSimulation();
    }
  }, [isOpen, resetSimulation]);

  const renderEmptyState = () => (
    <Box sx={simulatorEmptyStateStyle}>
      <Typography sx={simulatorEmptyTextStyle}>
        {translate('approvalSimulator.enterSampleInputs')}
      </Typography>
    </Box>
  );

  const renderResults = () => {
    if (!simulationResult) {
      return null;
    }

    return (
      <Box sx={resultsSectionStyle}>
        <ResultsSummary summary={simulationResult.summary} />
        <MatchedRulesSection rules={simulationResult.matchedRules} />
        {simulationResult.suppressedRules.length > 0 && (
          <SuppressedRulesSection
            rules={simulationResult.suppressedRules}
            exclusiveRuleName={simulationResult.exclusiveRuleName}
          />
        )}
        {simulationResult.finalGates.length > 0 && (
          <FinalGatesSection gates={simulationResult.finalGates} />
        )}
        {simulationResult.evaluationTraces.length > 0 && (
          <EvaluationTraceSection traces={simulationResult.evaluationTraces} />
        )}
      </Box>
    );
  };

  const renderFooter = () => (
    <>
      <Typography sx={simulatorStatusStyle}>
        {translate('approvalSimulator.testingAgainst', {
          count: activeRulesCount.toString()
        })}
      </Typography>
      <Button color="primary" sx={simulatorCloseButtonStyle} onClick={onClose}>
        {translate('approvalSimulator.close')}
      </Button>
    </>
  );

  return (
    <Modal
      headerTitle={translate('approvalSimulator.title')}
      isOpen={isOpen}
      hide={onClose}
      maxWidth={false}
      sx={modalSx}
      footer={renderFooter()}
    >
      <Box sx={simulatorContainerStyle}>
        <ApprovalRuleSimulatorForm
          expenseTypes={expenseTypes}
          counterparties={counterparties}
          currencies={currencies}
          defaultCurrency={actualDefaultCurrency}
          onSubmit={handleSimulateClick}
          onChange={handleSimulationChange}
        />

        <Box sx={simulatorContentStyle}>
          {!simulationResult ? renderEmptyState() : renderResults()}
        </Box>
      </Box>
    </Modal>
  );
};
